import { formatMoney } from "@Utils/Functions";
import moment from "moment";
import { Listing } from "shared-components";
import { translations } from "./Translations";
import { FiltersValues } from "shared-components/Contexts/Filters/model";
import { ListingData } from "shared-components/ViewFragments/SearchPage/ListingCard";
import { useContext } from "react";
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context";

export const thereIsTranslations = countryCode => {
	const keyExist = Object.keys(translations).includes(countryCode);
	return keyExist;
};

export const firstLetterToUppercase = word => {
	const editedWord = word?.charAt(0)?.toUpperCase() + word?.slice(1);
	return editedWord;
};

export const getStratumText = stratum => {
	switch (stratum) {
		case 110:
			return "Sin especificar";
		case 100:
		case 7:
			return "Campestre";
		case 0:
			return "Estrato 0";
		case 1:
			return "Estrato 1";
		case 2:
			return "Estrato 2";
		case 3:
			return "Estrato 3";
		case 4:
			return "Estrato 4";
		case 5:
			return "Estrato 5";
		case 6:
			return "Estrato 6";
		default:
			return "Sin especificar";
	}
};

export const getFormattedFilters = filters => {
	if (filters?.locations && !!filters?.locations?.length) {
		const correctLocations = [
			{
				type: filters.locations[0]?.type,
				id: filters.locations[0]?.id,
				name: filters.locations[0]?.name,
			},
		];

		return {
			...filters,
			locations: correctLocations,
		};
	} else return filters;
};

export const formattedLocationsFilter = location => {
	return [
		{
			type: location?.type,
			id: location?.id,
			name: location?.name,
			estate: location?.estate,
		},
	];
};

export const getProjectCommertialUnitsOrdered = ({
	listing,
	filters,
}: {
	listing: Listing | ListingData;
	filters?: FiltersValues;
}) => {
	if (!listing.isProject) return null;
	if (!listing.commercial_units) return null;
	let commertialUnits = listing.commercial_units.sort((a, b) => a.price.amount - b.price.amount);

	if (filters) {
		const { minPrice, maxPrice, bedrooms, bathrooms, m2Min, m2Max, order } = filters;

		commertialUnits = [...commertialUnits].filter(unit => {
			let isValid = true;

			if (minPrice && unit.price.amount < minPrice) {
				isValid = false;
			}

			if (maxPrice && unit.price.amount > maxPrice) {
				isValid = false;
			}

			if (bedrooms && bedrooms?.length > 0) {
				if (filters.bedroomsExactMode && !bedrooms.includes(unit.bedrooms)) {
					isValid = false;
				} else if (!filters?.bedroomsExactMode && unit.bedrooms < bedrooms?.[0]) {
					isValid = false;
				}
			}

			if (bathrooms && bathrooms?.length > 0) {
				if (filters.bathroomsExactMode && !bathrooms.includes(unit.bathrooms)) {
					isValid = false;
				} else if (!filters?.bathroomsExactMode && unit.bathrooms < bathrooms?.[0]) {
					isValid = false;
				}
			}

			if (m2Min && unit.m2 < m2Min) {
				isValid = false;
			}

			if (m2Max && unit.m2 > m2Max) {
				isValid = false;
			}

			return isValid;
		});

		if (order) {
			switch (order) {
				case 0:
					commertialUnits.sort((a, b) => b.price.amount - a.price.amount);
					break;
				case 4:
					commertialUnits.sort((a, b) => a.m2 - b.m2);
					break;
				case 5:
					commertialUnits.sort((a, b) => b.m2 - a.m2);
					break;
				default:
					break;
			}
		}
	}

	return commertialUnits;
};

export const getProjectRangePrices = ({
	listing,
	filters,
}: {
	listing: Listing | ListingData;
	filters?: FiltersValues;
}) => {
	const commercialUnits = getProjectCommertialUnitsOrdered({ listing, filters });

	if (commercialUnits && !!commercialUnits.length) {
		const minPrice = { ...commercialUnits[0].price, currency: listing.price.currency };
		const maxPrice = {
			...commercialUnits[commercialUnits.length - 1].price,
			currency: listing.price.currency,
		};

		return { minPrice, maxPrice };
	} else {
		return { minPrice: null, maxPrice: null };
	}
};

function cleanSpecialCharacters(string) {
	if (!string) return null;

	string = string.replace(/[ñÑ]/g, "n");
	string = string.replace(/[áÁàÀâÂäÄãÃ]/g, "a");
	string = string.replace(/[éÉèÈêÊëË]/g, "e");
	string = string.replace(/[íÍìÌîÎïÏ]/g, "i");
	string = string.replace(/[óÓòÒôÔöÖõÕ]/g, "o");
	string = string.replace(/[úÚùÙûÛüÜ]/g, "u");
	string = string.replace(/[ýÝÿ]/g, "y");
	string = string.replace(/[çÇ]/g, "c");
	string = string.replace(/ - /g, "-");
	string = string.replace(/-\//g, "/");
	string = string.replace(/ \/ /g, "-y-");
	string = string.replace(/[.,]/g, "");
	string = string.replace(/[()]/g, "");
	string = string.replace(/!/g, "");
	string = string.replace(/'/g, "");
	string = string.replace(/\//g, "");
	string = string.replace(/--/g, "-");
	string = string.replace(/_/g, "-");
	string = string.replace(/\s+/g, "-");
	string = string.replace(/\s+/g, "");
	return string.toLowerCase();
}

export const getInmoLink = (id, name) => {
	let formattedName = cleanSpecialCharacters(name);
	let url = `/inmobiliarias/perfil/${id}-${formattedName}`;

	return url;
};

export const getOptionsSelectedText = (filterTypeObj, filterType, filterIsProject = false) => {
	if (Array.isArray(filterType)) {
		return filterTypeObj
			.filter(filter => filterType.includes(filter.value))
			.map(v => v.text)
			.join(", ");
	} else if (typeof filterType === "number") {
		let currentFilter = [];
		const projectsInOperationType = filterTypeObj.filter(type => type.value === 10).length; //ID de proyectos

		if (filterIsProject && !!projectsInOperationType)
			currentFilter = filterTypeObj.filter(filter => filter.projects === filterIsProject);
		else currentFilter = filterTypeObj.filter(filter => filter.value === filterType);

		const projectFilter = filterIsProject
			? currentFilter.find(filter => filter.projects)
			: null;

		return (projectFilter || currentFilter[0]).text;
	} else return "";
};

export const labelForBedroomsAndBathrooms = (filtersTags, showRoomsText = false) => {
	const { translations } = useContext(ConfigStateContext);
	const dormsText = showRoomsText ? "ambs." : `${translations.habs}.`;
	const capitalizedDormsText = dormsText.charAt(0).toUpperCase() + dormsText.slice(1);

	if (
		!filtersTags?.bedrooms?.length &&
		!filtersTags?.rooms?.length &&
		!filtersTags.bathrooms?.length
	) {
		return capitalizedDormsText + " y baños";
	}

	let result = "";

	if (!!filtersTags?.bedrooms?.length) {
		if (filtersTags?.bedrooms.length === 1) {
			let text = "";

			if (filtersTags?.bedrooms[0].value === 0) text = "Mono";
			else text = filtersTags?.bedrooms[0].value;

			text += " " + capitalizedDormsText + " ";
			result = text;
		} else {
			result = filtersTags?.bedrooms
				.reduce((acc, curr) => {
					let text = "";

					if (curr.value === 0) text = "Mono";
					else text = curr.value;

					return (acc += text + ", ");
				}, "")
				.replace(/, $/, "");

			result = result.replace(/, (\d+)$/, " y $1") + " " + capitalizedDormsText + " ";
		}

		if (!filtersTags?.bedroomsExactMode?.value) {
			result = result.replace(/(\d+|Mono)/g, "$1+");
		}
	} else if (!!filtersTags?.rooms?.length) {
		if (filtersTags?.rooms.length === 1) {
			result = filtersTags?.rooms[0].text;
		} else {
			result = filtersTags?.rooms
				.reduce((acc, curr) => (acc += curr.value + ", "), "")
				.replace(/, $/, "");
			result = result.replace(/, (\d+)$/, " y $1") + " " + "ambientes";
		}

		if (!filtersTags?.bedroomsExactMode?.value) {
			result = result.replace(/(\d+)/g, "$1+");
		}
	} else {
		result = capitalizedDormsText;
	}

	if (!!filtersTags?.bathrooms?.length) {
		if (result !== "") {
			result += " / ";
		}

		let result_baths = "";
		if (filtersTags?.bathrooms.length === 1) {
			result_baths = filtersTags?.bathrooms[0].value + " " + "baños";
		} else {
			result_baths = filtersTags?.bathrooms
				.reduce((acc, curr) => (acc += curr.value + ", "), "")
				.replace(/, $/, "");
			result_baths = result_baths.replace(/, (\d+)$/, " y $1") + " " + "baños";
		}
		if (!filtersTags?.bathroomsExactMode?.value) {
			result_baths = result_baths.replace(/(\d+)/g, "$1+");
		}
		result = result + result_baths;
	} else {
		result += "/ Baños";
	}

	return result === "" ? capitalizedDormsText + " y baños" : result;
};

export const getLabelSeasonsAndDates = filtersTags => {
	const dateFromFormatted = filtersTags?.dateFrom
		? moment(filtersTags?.dateFrom?.value, "YYYY/MM/DD").format("DD/MM/YYYY")
		: "";
	const dateToFormatted = filtersTags?.dateTo
		? moment(filtersTags?.dateTo?.value, "YYYY/MM/DD").format("DD/MM/YYYY")
		: "";

	if (filtersTags?.dateFrom && filtersTags?.dateTo)
		return `${dateFromFormatted} al ${dateToFormatted}`;
	if (filtersTags?.dateFrom && !filtersTags?.dateTo) return `Desde ${dateFromFormatted}`;
	if (!filtersTags?.dateFrom && filtersTags?.dateTo) return `Hasta ${dateToFormatted}`;
	if (filtersTags?.season) return filtersTags?.season?.text;

	return "Fechas";
};

export const getLabelPriceRange = (minPrice, maxPrice, currency) => {
	const formattedMin = formatMoney(minPrice);
	const formattedMax = formatMoney(maxPrice);
	let text = "";

	if (formattedMin && formattedMax) {
		text = `${currency} ${formattedMin} - ${currency} ${formattedMax}`;
	} else if (formattedMin) {
		text = `Desde ${currency} ${formattedMin}`;
	} else if (formattedMax) {
		text = `Hasta ${currency} ${formattedMax}`;
	} else {
		text = "Precio";
	}

	return text;
};

export const getLabelSurfaceRange = (m2Min, m2Max) => {
	if (m2Min && !m2Max) {
		return m2Min?.text;
	}
	if (!m2Min && m2Max) {
		return m2Max?.text;
	}
	if (m2Min && m2Max) {
		return (
			m2Min?.text.replace(/[^0-9\.]/g, "") +
			" - " +
			m2Max?.text.replace(/[^0-9\.]/g, "") +
			" m²"
		);
	}
	return "m2 Terreno";
};

export const formatNumber = num => {
	return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

export const validateRange = (min: string | number, max: string | number, message: string) => {
	if (Number(min) && Number(max) && Number(min) > 0 && Number(max) > 0) {
		return Number(min) > Number(max) ? message : "";
	}
	return "";
};

export const formatPhone = ({
	phone,
	countryCode = "UY",
}: {
	phone: string;
	countryCode?: string;
}): string => {
	if (countryCode !== "UY") {
		return phone;
	}

	const cleanPhone = phone.replace(/\D/g, "");
	const hasCountryCode = cleanPhone.startsWith("598");
	let localPhone = hasCountryCode ? cleanPhone.slice(3) : cleanPhone;
	if (hasCountryCode) localPhone = localPhone.startsWith("0") ? localPhone : `0${localPhone}`;

	if (localPhone.length < 7 || localPhone.length > 9) {
		return phone;
	}

	if (
		(localPhone.length === 8 && localPhone.startsWith("9")) ||
		(localPhone.length === 9 && localPhone.startsWith("0"))
	) {
		return `${localPhone.slice(0, 3)} ${localPhone.slice(3, 6)} ${localPhone.slice(6)}`;
	}

	if (localPhone.length === 8 && localPhone.startsWith("2")) {
		return `${localPhone.slice(0, 4)} ${localPhone.slice(4, 6)} ${localPhone.slice(6)}`;
	}

	if (localPhone.length === 7) {
		return `${localPhone.slice(0, 3)} ${localPhone.slice(3, 5)} ${localPhone.slice(5)}`;
	}

	if (hasCountryCode && localPhone.length === 9 && localPhone.startsWith("9")) {
		return `+598 ${localPhone.slice(0, 2)} ${localPhone.slice(2, 5)} ${localPhone.slice(5)}`;
	}

	if (hasCountryCode && localPhone.length === 8 && localPhone.startsWith("2")) {
		return `+598 ${localPhone.slice(0, 4)} ${localPhone.slice(4, 6)} ${localPhone.slice(6)}`;
	}

	return phone;
};

export const htmlToPlainText = html => {
	if (!html) return "";

	const text = html.replace(/<\/?[^>]+(>|$)/g, "");

	// Entidades HTML comunes, agregar más de ser necesario sino evaluar el uso de una librería
	const entities = {
		"&amp;": "&",
		"&lt;": "<",
		"&gt;": ">",
		"&quot;": '"',
		"&#39;": "'",
	};

	return text.replace(/&[a-zA-Z0-9#]+;/g, entity => entities[entity] || entity);
};

export const generateUniqueId = () => {
	const timestamp = Date.now().toString(36); // Fecha actual en base 36
	const random = Math.random()
		.toString(36)
		.substring(2, 8); // Número aleatorio en base 36
	return `${timestamp}-${random}`;
};

export const hexToRGBA = (hex, opacity) => {
	hex = hex.replace("#", "");

	const r = parseInt(hex.slice(0, 2), 16);
	const g = parseInt(hex.slice(2, 4), 16);
	const b = parseInt(hex.slice(4, 6), 16);

	return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export const getOptionLabel = option => {
	let label = "";

	label = option.name;

	if (option.type === "STATE") {
		label += ""; // option.country[0].name --> En UY no se pone el país después del departamento
	} else if (option.type === "NEIGHBOURHOOD") {
		label += `${option.city && !!option.city.length ? ", " + option.city[0].name : ""}${
			option.estate ? ", " + option.estate.name : ""
		}${option.zone && !!option.zone.length ? ", " + option.zone[0].name : ""}`;
	} else if (option.type === "CITY") {
		label += ", " + option.estate.name;
	} else {
		label += `${option.city && !!option.city.length ? ", " + option.city[0].name : ""}${
			option.estate ? ", " + option.estate.name : ""
		}`;
	}

	return label;
};

const useMainLocation = (countryName, location, filterLocations) => {
	const mainLocationExists =
		location?.location_main !== undefined && location?.location_main?.id !== undefined;

	if (filterLocations && countryName === "Colombia")
		return (
			mainLocationExists &&
			filterLocations.some(item => item.id === location.location_main?.id)
		);
	else return mainLocationExists;
};

export const getLocationLabel = (countryName, locations, filters = null) => {
	let label = "";
	const location = Array.isArray(locations) ? locations[0] : locations;
	const isValidMainLocation = useMainLocation(countryName, location, filters?.locations);
	const city = location?.city ? location?.city[0]?.name : null;
	const state = location?.state ? location?.state[0]?.name : null;
	const neighbourhood = location?.neighbourhood ? location?.neighbourhood[0]?.name : null;
	const commune = location?.commune ? location?.commune[0]?.name : null;
	const locality = location?.locality ? location?.locality[0]?.name : null;
	const zone = location?.locality ? location?.zone[0]?.name : null;

	if (isValidMainLocation) {
		const locationMain = location?.location_main;

		if (["neighbourhood", "commune", "zone", "locality"].includes(locationMain.location_type)) {
			return `${locationMain.name}${city ? `, ${city}` : ""}${state ? `, ${state}` : ""}`;
		}
		if (locationMain.location_type === "city") {
			return `${locationMain.name}${state ? `, ${state}` : ""}`;
		}
		if (locationMain.location_type === "state") {
			return locationMain.name;
		}
	} else if (filters?.locations && !!filters?.locations.length && countryName === "Colombia") {
		const locationType = filters?.locations[0].type.toLowerCase();
		let correctMatch = filters?.locations.filter(item => {
			const match = location[locationType]?.find(l => l?.id === item?.id);
			return match;
		});
		correctMatch = correctMatch[0];
		const locationName = correctMatch?.name;

		if (["NEIGHBOURHOOD", "COMMUNE", "ZONE", "LOCALITY"].includes(correctMatch?.type)) {
			return `${locationName}${city ? `, ${city}` : ""}${state ? `, ${state}` : ""}`;
		}
		if (correctMatch?.type === "CITY") {
			return `${locationName}${state ? `, ${state}` : ""}`;
		}
		if (correctMatch?.type === "STATE") {
			return locationName;
		}
	} else if (neighbourhood && !!Object.keys(neighbourhood).length) {
		return `${neighbourhood}${city ? `, ${city}` : ""}${state ? `, ${state}` : ""}`;
	} else if (commune && !!Object.keys(commune).length) {
		return `${commune}${city ? `, ${city}` : ""}`;
	} else if (locality && !!Object.keys(locality).length) {
		return `${locality}${city ? `, ${city}` : ""}${state ? `, ${state}` : ""}`;
	} else if (zone && !!Object.keys(zone).length) {
		return `${zone}${city ? `, ${city}` : ""}${state ? `, ${state}` : ""}`;
	} else if (city && !!Object.keys(city).length) {
		return `${city ? `${city}` : ""}${state ? `, ${state}` : ""}`;
	} else if (state && !!Object.keys(state).length) {
		return state;
	} else {
		return countryName;
	}

	return label;
};

export const getOperationValue = option => {
	return option.projects ? 1 : option.value;
};

export const getInitialContactForm = (isLoggedIn, dataUser) => {
	const contactForm =
		typeof window !== "undefined" ? window.localStorage.getItem("contactForm") : null;

	if (isLoggedIn && dataUser) {
		return {
			name: dataUser?.me?.name,
			phone: dataUser?.me?.phone,
			email: dataUser?.me?.email,
			fincatyc: dataUser?.me?.is_terms_and_conditions_accepted,
		};
	}

	if (contactForm) {
		const formParsed = JSON.parse(contactForm);

		return {
			name: formParsed.name,
			phone: formParsed.phone,
			email: formParsed.email,
			fincatyc: false,
		};
	}

	return {
		name: "",
		phone: "",
		email: "",
		fincatyc: false,
	};
};

